import * as React from "react";
import * as PropTypes from "prop-types";
import styled from "styled-components";

import { createGlobalStyle } from "../../../../styles/style-utils";
import { Metrics } from "../../vars";
import { Link } from "../../../Link";
import { getNodeText } from "../../../../utils";

export interface MenuItemLinkProps {
  href: string;
  parentMenuId: string;
  children?: React.ReactNode;
  className?: string;
  onClick?: (any) => any;
  disableTracking?: boolean;
}

const propTypes: { [k in keyof MenuItemLinkProps]: any } = {
  href: PropTypes.string.isRequired,
  parentMenuId: PropTypes.string.isRequired,
  children: PropTypes.any,
  className: PropTypes.string,
  onClick: PropTypes.func,
  disableTracking: PropTypes.bool,
};

const CustomCloseIcon = createGlobalStyle`
  div[role="menu"][aria-label="Plans & Properties"] .md-icon[role="closeDropdownButton"] {
    color: #ababab;
  }
`;

const MenuItemLink = ({
  children,
  className,
  disableTracking,
  href,
  onClick,
  parentMenuId,
}: MenuItemLinkProps) => {
  // eslint-disable-next-line no-negated-condition
  return !disableTracking ? (
    <Link
      className={className}
      data-id={`${parentMenuId}:${getNodeText(children)}`}
      href={href}
      onClick={onClick}
      trackingType="myAccountNavMenuItem"
      trackingLabel={`${parentMenuId}:${getNodeText(children)}`}
    >
      {children}
    </Link>
  ) : (
    <a className={className} href={href} onClick={onClick}>
      {children}
    </a>
  );
};

export const NavbarDropdownMenuItem: React.FC<
  Omit<MenuItemLinkProps, "onClick">
> = ({ children, className, disableTracking, href, parentMenuId }) => (
  <div className={className} role="menuItem" aria-label={getNodeText(children)}>
    {href.includes("my-account/home/update-plan/move-house.html") ? (
      <MenuItemLink
        className="analytics-nav"
        disableTracking={disableTracking}
        // eslint-disable-next-line no-script-url
        href="javascript:void(0)"
        onClick={() => testTarget(href)}
        parentMenuId={parentMenuId}
      >
        {children}
      </MenuItemLink>
    ) : (
      <MenuItemLink
        className="analytics-nav"
        disableTracking={disableTracking}
        href={href}
        parentMenuId={parentMenuId}
      >
        {children}
        {href === "/my-account/offers" && <CustomCloseIcon />}
      </MenuItemLink>
    )}
  </div>
);

const testTarget = (href) => {
  if (
    global.adobe &&
    global.adobe.target &&
    typeof global.adobe.target.getOffer === "function" &&
    typeof global.adobe.target.applyOffer === "function"
  ) {
    global.adobe.target.getOffer({
      mbox: "MA-1",
      success: function (offer) {
        if (offer && offer.length) {
          global.adobe.target.applyOffer({
            mbox: "MA-1",
            selector: "body",
            offer,
          });
        } else {
          global.location.pathname = href;
        }
      },
      error: function (status, error) {
        console.error("Error on A/B testing....", status, error);
        location.pathname = href;
      },
    });
  }
};

NavbarDropdownMenuItem.propTypes = propTypes;

export default styled(NavbarDropdownMenuItem)`
  display: block;
  padding: ${Metrics.Padding.NAVBAR_DROPDOWN_MENU_ITEM_Y}px 0;

  a,
  a:visited {
    color: ${(p) => p.theme.colors.grey500};
    font-size: ${Metrics.FontSizes.NAVBAR_DROPDOWN_MENU_ITEM_TEXT}px;
    line-height: ${Metrics.LineHeights.NAVBAR_DROPDOWN_MENU_ITEM_TEXT}px;
    text-decoration: none;
  }

  a:hover,
  a:active {
    color: ${(p) => p.theme.colors.primary};
  }

  &:first-child {
    padding-top: 0;
  }

  &:last-child {
    padding-bottom: 0;
  }

  ${(p) =>
    p.href === "/my-account/offers" &&
    `
    position: relative;
    margin-top: ${Metrics.Padding.NAVBAR_DROPDOWN_MENU_ITEM_Y}px;
    padding-top: ${Metrics.Padding.NAVBAR_DROPDOWN_MENU_ITEM_Y * 1.5}px;
    border-top: 1px solid ${p.theme.colors.grey100};

    a {
      font-weight: 600;

      &:before, &:after {
        display: inline-block;
        content: ' ';
        width: 24px;
        height: 24px;
        background-size: cover;
        background-position: left center;
      }

      &:before {
        position: relative;
        top: 6px;
        margin-right: 8px;
        background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' height='24px' width='24px' fill='%23ffb432'%3E%3Cg%3E%3Cpath d='M0,0h24v24H0V0z' fill='none'/%3E%3C/g%3E%3Cg%3E%3Cpath d='M21.41,11.41l-8.83-8.83C12.21,2.21,11.7,2,11.17,2H4C2.9,2,2,2.9,2,4v7.17c0,0.53,0.21,1.04,0.59,1.41l8.83,8.83 c0.78,0.78,2.05,0.78,2.83,0l7.17-7.17C22.2,13.46,22.2,12.2,21.41,11.41z M6.5,8C5.67,8,5,7.33,5,6.5S5.67,5,6.5,5S8,5.67,8,6.5 S7.33,8,6.5,8z'/%3E%3C/g%3E%3C/svg%3E");    
      }

      &:after {
        position: absolute;
        right: 0;
        top: 50%;
        margin-top: -4px;
        background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' height='24px' width='24px' fill='%23ec0000'%3E%3Cpath d='M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z'%3E%3C/path%3E%3C/svg%3E");
      }
    }  
  `}
`;
